import { z } from "zod";
import {
  CalibrationDecision,
  CalibrationPhase,
  DataSource,
  InterruptionReason,
  QualityClass,
  SelfReportedVision,
  StimulusKind,
  type StimulusMix,
  TrialStatus,
  VisionCorrection,
} from "./enums.js";
import type { StudySettings } from "./study.js";
import { VideoPlaybackMeta } from "./video.js";

/** Límite de muestras por ensayo (≈ 40 Hz durante el video más largo permitido, 180 s, con holgura). */
export const MAX_SAMPLES_PER_TRIAL = 8000;
/** Límite absoluto de coordenadas aceptadas (px CSS). Valores mayores se rechazan como carga inválida. */
export const MAX_ABS_COORDINATE = 100_000;

/** Metadatos no identificatorios del dispositivo y contexto visual. */
export const DeviceMeta = z
  .object({
    userAgent: z.string().max(400),
    browserSupported: z.boolean(),
    screenWidth: z.number().int().min(0).max(20000),
    screenHeight: z.number().int().min(0).max(20000),
    viewportWidth: z.number().int().min(0).max(20000),
    viewportHeight: z.number().int().min(0).max(20000),
    devicePixelRatio: z.number().positive().max(10),
    /** Zoom estimado del navegador (outerWidth / innerWidth), puede ser aproximado. */
    estimatedZoom: z.number().positive().max(10).nullable(),
    lighting: z.enum(["buena", "regular", "mala", "no_sabe"]),
    distanceCm: z.number().int().min(20).max(200).nullable(),
    fullscreenGranted: z.boolean(),
    isTestMode: z.boolean(),
  })
  .strict();
export type DeviceMeta = z.infer<typeof DeviceMeta>;

export const PublicSessionCreate = z
  .object({
    consent: z.literal(true),
    consentVersion: z.string().min(1).max(40),
    ageYears: z.number().int().min(0).max(130),
    visionCorrection: VisionCorrection,
    selfReportedVision: SelfReportedVision,
    source: DataSource.default("webgazer"),
    deviceMeta: DeviceMeta,
  })
  .strict();
export type PublicSessionCreate = z.infer<typeof PublicSessionCreate>;

export interface PublicSessionCreated {
  sessionId: string;
  participantCode: string;
  /** Token Bearer de vida corta para endpoints públicos de esta sesión. */
  accessToken: string;
  accessTokenExpiresAt: string;
  group: { code: string; name: string };
  source: DataSource;
}

export interface PublicStudyDto {
  code: string;
  title: string;
  description: string;
  consentVersion: string;
  consentText: string | null;
  /** Composición de los estímulos activos (para los textos de las pantallas). */
  stimulusKind: StimulusMix;
  imageCount: number;
  estimatedMinutes: number;
  /** Instalación en modo demo: participaciones limitadas. */
  demo: { enabled: boolean; participationsLeft: number | null };
  /** Días de retención de las sesiones (`deleteAfterDays`), mostrado en el consentimiento. */
  retentionDays: number;
  contactInfo: string;
  dataController: string;
  /** Rangos aceptados (para informar sin revelar datos de otras personas). */
  groups: Array<{ code: string; name: string; minAge: number; maxAge: number }>;
  syntheticCaptureAllowed: boolean;
}

export interface ProtocolImageDto {
  imageId: string;
  sequenceIndex: number;
  kind: StimulusKind;
  naturalWidth: number;
  naturalHeight: number;
  mimeType: string;
  sizeBytes: number;
  /** Solo videos. */
  durationMs: number | null;
  fps: number | null;
  hasAudio: boolean | null;
}

export interface SessionProtocolDto {
  sessionId: string;
  studyCode: string;
  source: DataSource;
  stimulusKind: StimulusMix;
  settings: StudySettings;
  orderSeed: string;
  images: ProtocolImageDto[];
  /** Ya se registró la validación con punto en movimiento (no se repite al reanudar). */
  pursuitDone: boolean;
}

/** Coordenadas de pantalla en px CSS. `null` si el rastreador no produjo predicción. */
export const RawGazeSample = z
  .object({
    xScreen: z.number().min(-MAX_ABS_COORDINATE).max(MAX_ABS_COORDINATE).nullable(),
    yScreen: z.number().min(-MAX_ABS_COORDINATE).max(MAX_ABS_COORDINATE).nullable(),
    tMs: z.number().min(-60_000).max(600_000),
    /**
     * Solo video: tiempo del video (ms) del fotograma visible cuando se tomó la muestra.
     * `null` si aún no se había presentado ningún fotograma.
     */
    videoTimeMs: z.number().min(0).max(600_000).nullable().optional(),
  })
  .strict();
export type RawGazeSample = z.infer<typeof RawGazeSample>;

export const ImageBoundsSchema = z
  .object({
    viewportWidth: z.number().positive().max(20000),
    viewportHeight: z.number().positive().max(20000),
    displayedLeft: z.number().min(-20000).max(20000),
    displayedTop: z.number().min(-20000).max(20000),
    displayedWidth: z.number().positive().max(20000),
    displayedHeight: z.number().positive().max(20000),
    naturalWidth: z.number().int().positive().max(100000),
    naturalHeight: z.number().int().positive().max(100000),
    devicePixelRatio: z.number().positive().max(10),
  })
  .strict();

export type ImageBoundsDto = z.infer<typeof ImageBoundsSchema>;

export const CalibrationPointResult = z
  .object({
    xPx: z.number(),
    yPx: z.number(),
    sampleCount: z.number().int().min(0),
    meanErrorPx: z.number().min(0).nullable(),
    percentInRoi: z.number().min(0).max(100).nullable(),
  })
  .strict();

export const CalibrationMetrics = z
  .object({
    method: z.string().min(1).max(300),
    calibrationPoints: z.number().int().min(0).max(50),
    pointsEvaluated: z.number().int().min(0).max(50),
    meanErrorPx: z.number().min(0).nullable(),
    medianErrorPx: z.number().min(0).nullable(),
    /** Error medio relativo a la diagonal del viewport (0–1). */
    meanErrorRelative: z.number().min(0).nullable(),
    percentInRoi: z.number().min(0).max(100).nullable(),
    roiRadiusPx: z.number().positive(),
    samplesPerSecond: z.number().min(0).nullable(),
    totalSamples: z.number().int().min(0),
    viewportWidth: z.number().positive(),
    viewportHeight: z.number().positive(),
    points: z.array(CalibrationPointResult).max(50),
  })
  .strict();
export type CalibrationMetrics = z.infer<typeof CalibrationMetrics>;

export const CalibrationRunCreate = z
  .object({
    clientCalibrationId: z.uuid(),
    phase: CalibrationPhase,
    startedAt: z.iso.datetime(),
    endedAt: z.iso.datetime(),
    metrics: CalibrationMetrics,
    decision: CalibrationDecision,
    overrideReason: z.string().min(3).max(500).optional(),
  })
  .strict();
export type CalibrationRunCreate = z.infer<typeof CalibrationRunCreate>;

export interface CalibrationRunCreated {
  calibrationId: string;
  sequence: number;
  /** Clasificación calculada en servidor con los umbrales del estudio. */
  classification: QualityClass;
  /** Si con esta corrida se puede iniciar/continuar la captura. */
  canProceed: boolean;
  manualOverride: boolean;
  duplicate: boolean;
}

export const TrialCreate = z
  .object({
    clientTrialId: z.uuid(),
    imageId: z.uuid(),
    sequenceIndex: z.number().int().min(0).max(10000),
    startedAt: z.iso.datetime(),
    durationActualMs: z.number().min(0).max(600_000),
    status: z.enum(["completed", "interrupted"]),
    interruptionReason: InterruptionReason.optional(),
    pageVisibilityState: z.enum(["visible", "hidden"]),
    bounds: ImageBoundsSchema,
    samples: z.array(RawGazeSample).max(MAX_SAMPLES_PER_TRIAL),
    endedNormally: z.boolean(),
    /** Obligatorio en estudios de video; prohibido en estudios de imágenes. */
    video: VideoPlaybackMeta.optional(),
  })
  .strict()
  .refine((t) => t.status !== "interrupted" || t.interruptionReason !== undefined, {
    message: "Un ensayo interrumpido requiere motivo",
    path: ["interruptionReason"],
  });
export type TrialCreate = z.infer<typeof TrialCreate>;

export interface TrialCreated {
  trialId: string;
  duplicate: boolean;
  sampleCountRaw: number;
  sampleCountValid: number;
  status: z.infer<typeof TrialStatus>;
}

export const SessionComplete = z.object({}).strict();

export const SessionAbort = z
  .object({
    status: z.enum(["aborted", "interrupted"]),
    reason: InterruptionReason,
    detail: z.string().max(500).optional(),
  })
  .strict();
export type SessionAbort = z.infer<typeof SessionAbort>;

